import { useMemo } from 'react';

export interface FaqItem {
  id: string;
  category: string;
  type: 'particulier' | 'entreprise';
  question: string;
  answer: string;
  tags: string[];
}

const FAQ_DATA: FaqItem[] = [
  // Catégorie 1: Organisation & fonctionnement (Particuliers)
  {
    id: 'tout-inclus',
    category: 'organisation',
    type: 'particulier',
    question: 'Est-ce que vous vous occupez de tout ?',
    answer: "Oui, c'est notre promesse. Maison Eridia prend en charge l'organisation complète de votre évènement : scénographie, coordination, prestataires, mobilier, installation, jusqu'au rangement final.",
    tags: ['organisation', 'service', 'complet']
  },
  {
    id: 'choix-prestations',
    category: 'organisation',
    type: 'particulier',
    question: 'Comment fonctionne le choix des prestations ?',
    answer: "Dans notre formulaire de devis, vous pouvez choisir soit un type d'évènement complet (avec prestations incluses), soit des prestations à la carte (traiteur, déco, animation, etc.). Chaque élément est ajustable à votre guise.",
    tags: ['prestations', 'devis', 'personnalisation']
  },
  {
    id: 'aide-conception',
    category: 'organisation',
    type: 'particulier',
    question: "Je n'ai pas d'idée précise, pouvez-vous m'aider à concevoir mon évènement ?",
    answer: 'Absolument. Nos équipes vous accompagnent à chaque étape avec des suggestions personnalisées selon votre thème, vos goûts et votre budget.',
    tags: ['accompagnement', 'conseil', 'sur-mesure']
  },
  {
    id: 'coordinateur-present',
    category: 'organisation',
    type: 'particulier',
    question: "Est-ce qu'un coordinateur est présent le jour de l'évènement ?",
    answer: "Oui, dès lors que vous optez pour une formule complète ou que la prestation l'exige. Un membre de notre équipe assure la coordination sur place pour que tout se déroule sans accroc.",
    tags: ['coordinateur', 'présence', 'organisation']
  },
  {
    id: 'thematique-precise',
    category: 'organisation',
    type: 'particulier',
    question: 'Pouvez-vous vous adapter à une thématique précise ?',
    answer: 'Tout à fait. Si vous avez un thème (couleurs, ambiance, film, pays…), nous adaptons la scénographie, les animations et les décorations autour de votre univers.',
    tags: ['thématique', 'personnalisation', 'scénographie']
  },
  // Catégorie 2: Lieux & logistique (Particuliers)
  {
    id: 'evenements-domicile',
    category: 'logistique',
    type: 'particulier',
    question: 'Organisez-vous des évènements dans différents types de lieux ?',
    answer: 'Oui, absolument ! Maison Eridia organise des évènements dans tous types de lieux en Île-de-France : domiciles privés (maisons, appartements, jardins, rooftops), lieux de réception, espaces atypiques, locaux d\'entreprise. Nous nous adaptons à vos envies et contraintes pour créer l\'ambiance parfaite.',
    tags: ['domicile', 'lieu', 'spécialisation']
  },
  {
    id: 'zone-intervention',
    category: 'logistique',
    type: 'particulier',
    question: "Organisez-vous des évènements ailleurs qu'en Île-de-France ?",
    answer: 'Pour garantir une qualité constante, nous opérons uniquement en Île-de-France pour le moment.',
    tags: ['zone', 'Île-de-France', 'qualité']
  },
  {
    id: 'meteo-mauvaise',
    category: 'logistique',
    type: 'particulier',
    question: 'Et si la météo est mauvaise le jour de mon évènement ?',
    answer: 'Nous proposons toujours des solutions de repli (tentes, mobilier intérieur, chauffage…). Nos équipes vérifient la météo 48h avant votre évènement et adaptent le dispositif.',
    tags: ['météo', 'solutions', 'adaptation']
  },
  {
    id: 'mobilier-insuffisant',
    category: 'logistique',
    type: 'particulier',
    question: "Que faire si je n'ai pas assez de mobilier chez moi ?",
    answer: 'Aucun souci, nous proposons la location de mobilier : chaises, tables, tentes, banquettes, mange-debout, nappes… tout est prévu pour transformer votre espace.',
    tags: ['mobilier', 'location', 'espace']
  },
  {
    id: 'logement-petit',
    category: 'logistique',
    type: 'particulier',
    question: 'Mon logement est petit, est-ce un problème ?',
    answer: "Pas du tout. Nous avons l'habitude de créer des évènements élégants même dans des espaces restreints, en misant sur l'optimisation et la scénographie intimiste.",
    tags: ['petit espace', 'optimisation', 'scénographie']
  },
  // Catégorie 3: Réservation & délais (Particuliers)
  {
    id: 'delai-reservation',
    category: 'reservation',
    type: 'particulier',
    question: "Combien de temps à l'avance faut-il réserver ?",
    answer: 'Idéalement 2 à 4 semaines avant. Pour les périodes chargées (été, décembre), nous vous conseillons de réserver dès que possible. Nous traitons aussi certaines demandes urgentes.',
    tags: ['délai', 'planification', 'urgence']
  },
  {
    id: 'confirmation-reservation',
    category: 'reservation',
    type: 'particulier',
    question: 'Comment puis-je confirmer ma réservation ?',
    answer: "Une fois le devis validé, vous recevez un lien sécurisé pour signer électroniquement et verser un acompte. C'est à ce moment-là que votre évènement est officiellement confirmé.",
    tags: ['confirmation', 'signature', 'acompte']
  },
  {
    id: 'modification-devis',
    category: 'reservation',
    type: 'particulier',
    question: 'Peut-on modifier sa demande après envoi du devis ?',
    answer: 'Oui, vous pouvez nous contacter pour ajuster les prestations, le nombre d\'invités ou les détails pratiques avant validation définitive.',
    tags: ['modification', 'ajustement', 'flexibilité']
  },
  {
    id: 'annulation-report',
    category: 'reservation',
    type: 'particulier',
    question: 'Est-ce que je peux annuler ou reporter mon évènement ?',
    answer: 'Oui, en fonction du délai et des conditions générales de vente. Contactez-nous rapidement, nous trouverons toujours une solution humaine et raisonnable.',
    tags: ['annulation', 'report', 'flexibilité']
  },
  // Catégorie 4: Budget & paiements (Particuliers)
  {
    id: 'budget-minimum',
    category: 'budget',
    type: 'particulier',
    question: 'À partir de quel budget peut-on organiser un évènement avec Maison Eridia ?',
    answer: 'Nos formules démarrent à 1 000 € TTC. Le tarif dépend du type d\'évènement, du nombre d\'invités, des prestations choisies et du niveau de gamme.',
    tags: ['budget', 'tarif', 'minimum']
  },
  {
    id: 'calcul-prix',
    category: 'budget',
    type: 'particulier',
    question: 'Comment sont calculés les prix ?',
    answer: 'Chaque prestation est tarifée selon son niveau de gamme (Essentiel, Confort, Signature) et le mode de facturation (forfait, par personne, par heure). Le formulaire de devis calcule automatiquement votre estimation TTC.',
    tags: ['prix', 'calcul', 'gamme']
  },
  {
    id: 'facilites-paiement',
    category: 'budget',
    type: 'particulier',
    question: 'Proposez-vous des facilités de paiement ?',
    answer: "Oui, nous proposons un paiement en deux fois : un acompte à la validation du devis, et le solde restant le jour de l'évènement.",
    tags: ['paiement', 'facilités', 'acompte']
  },
  // Catégorie 5: Personnalisation & offres (Particuliers)
  {
    id: 'personnalisation-traiteur',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Peut-on personnaliser le gâteau, les cocktails ou les boissons ?',
    answer: 'Oui, tous nos traiteurs proposent du sur-mesure : choix de goûts, de visuels, de formats… Vous pouvez même nous envoyer des inspirations.',
    tags: ['personnalisation', 'traiteur', 'sur-mesure']
  },
  {
    id: 'installation-complete',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Proposez-vous une installation complète ?',
    answer: "Oui, la mise en place et la désinstallation sont assurées par notre équipe. Vous n'avez rien à gérer, on s'occupe de tout.",
    tags: ['installation', 'service', 'complet']
  },
  {
    id: 'prestation-derniere-minute',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Peut-on ajouter une prestation de dernière minute ?',
    answer: "Oui, dans la limite des disponibilités logistiques. Nous faisons notre maximum pour vous proposer des ajouts, même à quelques jours de l'évènement.",
    tags: ['dernière minute', 'ajout', 'flexibilité']
  },
  {
    id: 'decoration-seule',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Est-il possible de choisir uniquement la décoration ?',
    answer: 'Bien sûr. Chaque prestation peut être réservée indépendamment (décoration seule, animation seule, traiteur seul, etc.).',
    tags: ['décoration', 'à la carte', 'indépendant']
  },
  // Catégorie 6: Enfants & familles (Particuliers)
  {
    id: 'animations-enfants',
    category: 'enfants',
    type: 'particulier',
    question: 'Proposez-vous des animations pour enfants ?',
    answer: "Oui. Maquillage, ateliers DIY, animations encadrées, mascottes, piñatas… Nous avons une gamme adaptée selon l'âge des enfants.",
    tags: ['animations', 'enfants', 'activités']
  },
  {
    id: 'enfants-invites',
    category: 'enfants',
    type: 'particulier',
    question: 'Est-ce que les enfants sont comptés comme des invités ?',
    answer: "Oui, chaque personne sur place est prise en compte dans l'organisation : assises, repas, sécurité… Pour les bébés ou enfants en bas âge, des options spécifiques peuvent être proposées.",
    tags: ['enfants', 'comptage', 'organisation']
  },
  {
    id: 'allergenes',
    category: 'enfants',
    type: 'particulier',
    question: 'Proposez-vous des options sans allergènes (gluten, lactose…) ?',
    answer: 'Oui, sur simple demande. Nos prestataires s\'adaptent à vos contraintes alimentaires (enfants et adultes).',
    tags: ['allergènes', 'sans gluten', 'contraintes alimentaires']
  },
  // Catégorie 7: Sécurité & conditions (Particuliers)
  {
    id: 'prestataires-assures',
    category: 'securite',
    type: 'particulier',
    question: 'Les prestataires sont-ils assurés et professionnels ?',
    answer: 'Oui, tous nos partenaires sont sélectionnés pour leur sérieux, leur ponctualité et leur couverture assurantielle. Vous êtes entre de bonnes mains.',
    tags: ['assurance', 'professionnels', 'sélection']
  },
  {
    id: 'prestataire-annule',
    category: 'securite',
    type: 'particulier',
    question: "Que se passe-t-il si un prestataire annule ?",
    answer: "Nous avons toujours des solutions de remplacement. En cas d'imprévu, nous vous informons immédiatement et proposons une alternative équivalente ou supérieure.",
    tags: ['annulation', 'remplacement', 'solution']
  },
  // Questions entreprises/professionnels
  {
    id: 'prestations-lieu-exterieur',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Proposez-vous des prestations sur lieu extérieur ?',
    answer: 'Oui, Maison Eridia intervient dans vos locaux, espaces loués ou lieux partenaires. Nous adaptons notre installation en fonction du lieu et de la configuration.',
    tags: ['lieu extérieur', 'locaux', 'adaptation']
  },
  {
    id: 'horaires-soiree-weekend',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Travaillez-vous en soirée ou le week-end ?',
    answer: "Absolument. Nos équipes sont disponibles selon les horaires de votre évènement, même en soirée ou le week-end. Des frais peuvent s'appliquer pour des prestations en dehors des horaires classiques.",
    tags: ['soirée', 'week-end', 'horaires', 'flexibilité']
  },
  {
    id: 'facturation-groupee',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-il possible de faire une facture mensuelle ou groupée ?',
    answer: 'Oui, pour nos clients réguliers ou sur demande préalable, nous pouvons proposer une facturation groupée avec conditions personnalisées.',
    tags: ['facturation', 'groupée', 'mensuelle', 'clients réguliers']
  },
  {
    id: 'brief-partiel-delai-court',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Peut-on organiser un évènement avec un brief partiel ou un délai court ?',
    answer: 'Nous étudions chaque demande au cas par cas. Plus vous nous contactez tôt, mieux nous pouvons personnaliser, mais nous pouvons aussi mobiliser rapidement nos équipes selon la disponibilité.',
    tags: ['brief partiel', 'délai court', 'urgence', 'flexibilité']
  },
  {
    id: 'entreprises-toute-taille',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-ce que Maison Eridia travaille avec des entreprises de toute taille ?',
    answer: 'Oui, de la start-up aux grandes entreprises. Nos offres sont modulables selon les besoins et le budget.',
    tags: ['start-up', 'grandes entreprises', 'modulable', 'budget']
  },
  {
    id: 'services-recurrents-abonnements',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Proposez-vous des services récurrents ou des abonnements ?',
    answer: "Oui, notamment pour les comités d'entreprise, les agences ou structures qui organisent des évènements réguliers. N'hésitez pas à nous écrire à ce sujet.",
    tags: ['récurrent', 'abonnement', 'comité entreprise', 'agences']
  },
  {
    id: 'test-prestations',
    category: 'entreprise',
    type: 'entreprise',
    question: "Peut-on tester vos prestations avant de s'engager ?",
    answer: 'Nous proposons parfois des démonstrations ou visites virtuelles selon les prestations. Contactez-nous pour en discuter.',
    tags: ['test', 'démonstration', 'visite virtuelle', 'engagement']
  },
  // Nouvelles questions particuliers
  {
    id: 'types-evenements-particuliers',
    category: 'personnalisation',
    type: 'particulier',
    question: "Quels types d'évènements organisez-vous pour les particuliers ?",
    answer: 'Nous organisons des anniversaires signature, mariages, demandes en mariage, Saint-Valentin… et plein d\'autres évènements sur mesure.',
    tags: ['types évènements', 'anniversaires', 'mariages', 'sur mesure']
  },
  {
    id: 'niveaux-prestations',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Est-ce que je peux choisir le niveau de prestation (Essentiel, Confort, Signature) ?',
    answer: 'Oui, nous proposons trois gammes adaptées à votre budget et vos envies. La gamme choisie s\'applique à l\'ensemble de votre évènement pour garantir une cohérence si vous le souhaitez, ou sinon vous pouvez faire vos sélections à la carte.',
    tags: ['niveaux', 'gammes', 'essentiel', 'confort', 'signature', 'budget', 'à la carte']
  },
  {
    id: 'fourniture-lieu',
    category: 'logistique',
    type: 'particulier',
    question: "Fournissez-vous aussi le lieu de l'évènement ?",
    answer: 'Nous pouvons organiser votre évènement dans le lieu de votre choix, mais également vous proposer une sélection de lieux partenaires (salles de réception, péniches, rooftops, jardins, domaines, restaurants… et plein d\'autres).',
    tags: ['lieu', 'partenaires', 'salles', 'péniches', 'rooftops', 'jardins', 'domaines']
  },
  {
    id: 'installation-rangement',
    category: 'personnalisation',
    type: 'particulier',
    question: "Est-ce que vous vous occupez de l'installation et du rangement ?",
    answer: 'Oui, toutes nos prestations incluent l\'installation et la désinstallation pour que vous profitiez pleinement de votre moment.',
    tags: ['installation', 'rangement', 'désinstallation', 'service complet']
  },
  {
    id: 'personnalisation-decoration',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Peut-on personnaliser la décoration ?',
    answer: 'Tout à fait ! Nous adaptons les couleurs, la thématique et certains éléments pour correspondre à vos envies et à l\'ambiance souhaitée.',
    tags: ['personnalisation', 'décoration', 'couleurs', 'thématique', 'ambiance']
  },
  {
    id: 'options-forfait',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Puis-je ajouter des options à mon forfait ?',
    answer: 'Oui, vous pouvez enrichir votre formule avec des options comme un photobooth, un gâteau personnalisé, un DJ, du mobilier spécifique, des fleurs supplémentaires, etc.',
    tags: ['options', 'photobooth', 'gâteau', 'DJ', 'mobilier', 'fleurs', 'enrichissement']
  },
  // Nouvelles questions professionnels
  {
    id: 'types-evenements-entreprises',
    category: 'entreprise',
    type: 'entreprise',
    question: "Quels types d'évènements organisez-vous pour les entreprises ?",
    answer: 'Nous organisons des séminaires, soirées d\'entreprise, journées / week-end d\'intégration, conférences, lancements de produits, repas d\'affaires, afterworks, team buildings… et plein d\'autres formats adaptés au monde professionnel.',
    tags: ['séminaires', 'soirées entreprise', 'intégration', 'conférences', 'lancements', 'repas affaires', 'afterworks', 'team building']
  },
  {
    id: 'location-lieux-professionnels',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-ce que vous proposez aussi la location de lieux pour les évènements professionnels ?',
    answer: 'Oui, nous travaillons avec un large réseau de partenaires (salles, hôtels, clubs, péniches, boîtes de nuit, rooftops, etc.) pour répondre à tous les besoins.',
    tags: ['location lieux', 'partenaires', 'salles', 'hôtels', 'clubs', 'péniches', 'rooftops']
  },
  {
    id: 'restauration-professionnelle',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Prenez-vous en charge la restauration ?',
    answer: 'Oui, nous proposons plusieurs formules traiteur : cocktails, buffets, menus assis, options halal, végétariennes, véganes… et plein d\'autres alternatives selon vos attentes.',
    tags: ['restauration', 'traiteur', 'cocktails', 'buffets', 'halal', 'végétarien', 'végan']
  },
  {
    id: 'animations-professionnelles',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Pouvez-vous intégrer des animations pour dynamiser un évènement professionnel ?',
    answer: 'Bien sûr, nous proposons des animations comme karaoké, DJ, ateliers créatifs, activités de team building, jeux interactifs et bien plus encore.',
    tags: ['animations', 'karaoké', 'DJ', 'ateliers', 'team building', 'jeux interactifs']
  },
  {
    id: 'accompagnement-cle-en-main',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Offrez-vous un accompagnement clé en main ?',
    answer: 'Oui, nous pouvons prendre en charge l\'intégralité de l\'évènement : organisation, logistique, décoration, son & lumière, personnel évènementiel, coordination le jour J.',
    tags: ['clé en main', 'organisation', 'logistique', 'son lumière', 'personnel', 'coordination']
  },
  {
    id: 'prestations-sur-mesure-budget',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Proposez-vous des prestations sur mesure selon le budget ?',
    answer: 'Absolument. Nous adaptons chaque projet en fonction de vos contraintes budgétaires et de vos objectifs pour maximiser l\'impact de votre évènement.',
    tags: ['sur mesure', 'budget', 'contraintes', 'objectifs', 'impact']
  }
];
// Ajout des nouvelles questions complémentaires
FAQ_DATA.push(
  // FAQ Particuliers (complément)  
  {
    id: 'gateaux-patisseries',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Est-ce que vous proposez aussi des gâteaux ou pâtisseries pour les évènements ?',
    answer: 'Oui, nous travaillons avec des partenaires traiteurs pour proposer gâteaux d\'anniversaire, pièces montées, buffets sucrés, cocktails et plein d\'autres gourmandises.',
    tags: ['gâteaux', 'pâtisseries', 'traiteur', 'buffets sucrés', 'anniversaire', 'pièces montées']
  },
  {
    id: 'location-materiel-seul',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Peut-on louer uniquement du matériel de décoration sans prestation complète ?',
    answer: 'Oui, grâce à nos partenaires nous proposons la location de décorations, mobilier, vaisselle, sonorisation, lumières et accessoires, avec ou sans installation.',
    tags: ['location', 'matériel', 'décoration', 'mobilier', 'vaisselle', 'sonorisation', 'lumières']
  },
  {
    id: 'minimum-invites',
    category: 'logistique',
    type: 'particulier',
    question: 'Y a-t-il un minimum d\'invités pour organiser un évènement ?',
    answer: 'Non, nous organisons aussi bien des dîners intimistes à 2 personnes que des fêtes pour 200 invités.',
    tags: ['minimum invités', 'dîners intimistes', 'fêtes', 'flexibilité']
  },
  {
    id: 'menage-apres-evenement',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Dois-je m\'occuper du ménage après l\'évènement ?',
    answer: 'Non, nous gérons l\'installation, la désinstallation et le rangement. Vous n\'avez qu\'à profiter de votre moment.',
    tags: ['ménage', 'rangement', 'désinstallation', 'service complet']
  },
  {
    id: 'animations-musicales',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Proposez-vous des animations musicales ?',
    answer: 'Oui, selon la formule choisie nous pouvons inclure DJ, musiciens live, karaoké, ou encore ambiance lounge.',
    tags: ['animations musicales', 'DJ', 'musiciens', 'karaoké', 'ambiance lounge']
  },
  {
    id: 'ballons-reutilisables',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Les ballons et décorations sont-ils réutilisables ?',
    answer: 'Certains éléments sont réutilisables, d\'autres (comme les ballons) sont à usage unique. Nous privilégions des décors durables et écoresponsables quand c\'est possible.',
    tags: ['ballons', 'réutilisable', 'décors durables', 'écoresponsables']
  },
  {
    id: 'delai-reservation-complement',
    category: 'reservation',
    type: 'particulier',
    question: 'Combien de temps à l\'avance faut-il réserver ?',
    answer: 'L\'idéal est de réserver 1 à 2 mois avant votre évènement, mais nous pouvons aussi organiser des prestations plus rapidement selon disponibilité.',
    tags: ['délai', 'réservation', 'planning', 'urgence', 'disponibilité']
  },
  {
    id: 'maquillage-mise-en-beaute',
    category: 'personnalisation',
    type: 'particulier',
    question: 'Est-ce que vous proposez aussi du maquillage ou de la mise en beauté ?',
    answer: 'Oui, nous avons des maquilleuses professionnelles pour mariages et autres évènements festifs.',
    tags: ['maquillage', 'mise en beauté', 'maquilleuses', 'mariages', 'festifs']
  },
  // FAQ Professionnels (complément)
  {
    id: 'evenement-locaux-entreprise',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-ce possible d\'organiser un évènement directement dans nos locaux ?',
    answer: 'Oui, nous adaptons la scénographie et la logistique à votre espace : bureaux, open space, rooftop, showroom, etc.',
    tags: ['locaux entreprise', 'bureaux', 'open space', 'rooftop', 'showroom', 'adaptation']
  },
  {
    id: 'personnel-evenementiel',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Fournissez-vous le personnel évènementiel ?',
    answer: 'Oui, nous proposons hôtes(ses), serveurs, animateurs, techniciens son & lumière et coordinateurs d\'évènements.',
    tags: ['personnel', 'hôtesses', 'serveurs', 'animateurs', 'techniciens', 'coordinateurs']
  },
  {
    id: 'charte-graphique-entreprise',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-ce que vous pouvez intégrer la charte graphique de notre entreprise ?',
    answer: 'Oui, nous personnalisons la décoration, les supports visuels et même les animations pour refléter l\'image de votre marque.',
    tags: ['charte graphique', 'personnalisation', 'marque', 'supports visuels', 'image entreprise']
  },
  {
    id: 'ateliers-thematiques-employes',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Est-il possible de proposer des ateliers thématiques pour les employés ?',
    answer: 'Oui, nous organisons des ateliers bien-être, créatifs, culinaires, ludiques ou sportifs adaptés à vos équipes.',
    tags: ['ateliers', 'bien-être', 'créatifs', 'culinaires', 'ludiques', 'sportifs', 'équipes']
  },
  {
    id: 'prestataires-certifies',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Travaillez-vous avec des prestataires certifiés (traiteur, technique, etc.) ?',
    answer: 'Oui, nous collaborons uniquement avec des partenaires fiables et expérimentés pour garantir une qualité irréprochable.',
    tags: ['prestataires certifiés', 'partenaires fiables', 'qualité', 'expérience', 'traiteur', 'technique']
  },
  {
    id: 'accompagnement-apres-evenement',
    category: 'entreprise',
    type: 'entreprise',
    question: 'Offrez-vous un accompagnement après l\'évènement (photos, vidéos, reporting) ?',
    answer: 'Oui, nous pouvons fournir un suivi complet avec photos, vidéos, analyses de satisfaction et recommandations pour vos prochains évènements.',
    tags: ['suivi', 'photos', 'vidéos', 'reporting', 'analyses satisfaction', 'recommandations']
  }
);

export const useFaqSearch = () => {
  const searchFaq = useMemo(() => {
    return (query: string): FaqItem[] => {
      if (!query.trim()) return [];
      
      const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 0);
      
      return FAQ_DATA.filter(item => {
        const searchableText = `${item.question} ${item.answer} ${item.tags.join(' ')}`.toLowerCase();
        
        // L'élément correspond si tous les termes de recherche sont trouvés
        return searchTerms.every(term => 
          searchableText.includes(term)
        );
      }).slice(0, 5); // Limiter à 5 résultats
    };
  }, []);

  const getAllFaq = useMemo(() => FAQ_DATA, []);

  return {
    searchFaq,
    getAllFaq
  };
};